export interface ConservatoryStyle {
  id: string;
  name: string;
  tagline: string;
  description: string;
  longDesc: string;
  priceFrom: string;
  priceRange: string;
  popular: boolean;
  badge?: string;
  image: string;
  features: string[];
  bestFor: string[];
  roofType: string;
  spaceRating: number;
  lightRating: number;
  budgetRating: number;
  category: "classic" | "premium" | "bespoke" | "specialist";
}

export const conservatoryStyles: ConservatoryStyle[] = [
  {
    id: "lean-to",
    name: "Lean-To",
    tagline: "Clean, simple & affordable",
    description: "The most versatile conservatory style — a single sloping roof that leans against the house wall. Works on any property and any budget.",
    longDesc: "Lean-to conservatories are the simplest and most cost-effective design. With a clean single-pitch roof that slopes away from the house, they are perfect for bungalows, properties with limited height, and anyone looking for a modern, minimalist aesthetic. Incredibly versatile — they work on virtually any home.",
    priceFrom: "£7,500",
    priceRange: "£7,500 – £18,000",
    popular: true,
    badge: "Most Affordable",
    image: "https://public.readdy.ai/ai/img_res/7f83202c3622b2c82922e216c4e0b5bd.jpg",
    features: ["Simplest design", "Suits any property style", "Excellent for bungalows", "Low maintenance", "Fast installation", "Slim profile"],
    bestFor: ["Bungalows", "Modern homes", "Smaller budgets", "Narrow plots"],
    roofType: "Single-pitch glass or polycarbonate",
    spaceRating: 3,
    lightRating: 4,
    budgetRating: 5,
    category: "classic",
  },
  {
    id: "edwardian",
    name: "Edwardian",
    tagline: "Maximum floor space, timeless design",
    description: "Square or rectangular footprint giving you the most usable floor space of any conservatory style. Clean lines suit both modern and traditional homes.",
    longDesc: "The Edwardian conservatory is the most space-efficient design available. Its square or rectangular shape means you get the maximum usable floor area relative to the footprint. With slim, elegant frames and a classic hipped roof, it looks at home on both period and modern properties.",
    priceFrom: "£9,000",
    priceRange: "£9,000 – £22,000",
    popular: true,
    badge: "Best Floor Space",
    image: "https://readdy.ai/api/search-image?query=Edwardian%20conservatory%20square%20rectangular%20white%20uPVC%20aluminium%20traditional%20uk%20home%20brick%20red%20garden%20extension%20double%20door%20glass%20roof%20light&width=800&height=550&seq=cons-edwardian&orientation=landscape",
    features: ["Maximum floor area", "Square/rectangular layout", "Clean elegant lines", "Suits all property types", "Easy to furnish", "Hipped roof"],
    bestFor: ["Family rooms", "Dining spaces", "Home offices", "All property types"],
    roofType: "Hipped glass roof",
    spaceRating: 5,
    lightRating: 4,
    budgetRating: 4,
    category: "classic",
  },
  {
    id: "hipped-back-edwardian",
    name: "Hipped-Back Edwardian",
    tagline: "Classic Edwardian with elegant rear slope",
    description: "All the floor-space benefits of the Edwardian design but with a hipped rear roof that adds visual depth and enhances the exterior appearance.",
    longDesc: "The Hipped-Back Edwardian combines the rectangular, maximally space-efficient layout of the standard Edwardian with a rear hip on the roof section. This gives the structure a more refined exterior finish, particularly when viewed from the garden, while maintaining every inch of that superb usable floor space.",
    priceFrom: "£11,000",
    priceRange: "£11,000 – £25,000",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=hipped%20back%20edwardian%20conservatory%20uk%20home%20white%20aluminium%20rectangular%20glass%20roof%20garden%20patio%20rear%20view%20traditional%20brick%20house%20extension&width=800&height=550&seq=cons-hipped-edw&orientation=landscape",
    features: ["Hipped rear roof detail", "Rectangular footprint", "Refined exterior look", "Maximum usable space", "Suits most elevations", "Premium appearance"],
    bestFor: ["Period properties", "Corner positions", "Large gardens", "Premium projects"],
    roofType: "Hipped-back glass roof",
    spaceRating: 5,
    lightRating: 4,
    budgetRating: 3,
    category: "classic",
  },
  {
    id: "victorian",
    name: "Victorian",
    tagline: "Classic bay front, timeless character",
    description: "The nation's favourite conservatory style. A beautiful multi-faceted bay front and ornate ridge detailing bring timeless Victorian elegance to any home.",
    longDesc: "The Victorian conservatory is by far the most popular design in the UK. Its multi-faceted bay front — usually 3 or 5 facets — combined with a steeply pitched roof creates a stunning focal point that suits brick-built homes beautifully. It feels grand, warm, and genuinely special.",
    priceFrom: "£10,000",
    priceRange: "£10,000 – £24,000",
    popular: true,
    badge: "Most Popular",
    image: "https://readdy.ai/api/search-image?query=Victorian%20conservatory%20three%20facet%20bay%20front%20traditional%20white%20uPVC%20aluminium%20red%20brick%20uk%20detached%20house%20garden%20ornate%20classic%20roof%20glazed&width=800&height=550&seq=cons-victorian&orientation=landscape",
    features: ["3 or 5 facet bay front", "Ornate ridge detailing", "Classic domed roof", "Traditional character", "Suits brick properties", "Iconic appearance"],
    bestFor: ["Traditional homes", "Dining rooms", "Sitting rooms", "Period properties"],
    roofType: "Multi-faceted pitched glass roof",
    spaceRating: 4,
    lightRating: 5,
    budgetRating: 3,
    category: "classic",
  },
  {
    id: "hipped-back-victorian",
    name: "Hipped-Back Victorian",
    tagline: "Victorian bay with a refined hipped roof",
    description: "The classic Victorian bay front combined with an elegant hipped rear. A particularly attractive choice for corner or exposed positions.",
    longDesc: "The Hipped-Back Victorian gives you everything that makes the Victorian style so loved — the bay front, the ornate ridge, the classic look — but adds a hipped roof at the rear for a more balanced, finished appearance when viewed from the side. Ideal for corner plots or anywhere the side of the conservatory is highly visible.",
    priceFrom: "£12,000",
    priceRange: "£12,000 – £26,000",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=hipped%20back%20Victorian%20conservatory%20uk%20traditional%20home%20white%20bay%20front%20ornate%20glass%20roof%20garden%20extension%20classic%20architecture%20brick%20detached&width=800&height=550&seq=cons-hipped-vic&orientation=landscape",
    features: ["Victorian bay front", "Hipped rear elevation", "Ideal for corner plots", "Multi-faceted bay", "Ornate ridge", "Premium finish"],
    bestFor: ["Corner plots", "Exposed positions", "Period homes", "High-visibility sides"],
    roofType: "Hipped-back multi-faceted roof",
    spaceRating: 4,
    lightRating: 5,
    budgetRating: 3,
    category: "classic",
  },
  {
    id: "gable",
    name: "Gable",
    tagline: "Dramatic height & maximum natural light",
    description: "A striking full-height front gable wall creates a sense of space and grandeur. Floods the interior with light and provides an impressive, cathedral-like feel.",
    longDesc: "The Gable conservatory is all about drama and light. Its vertical full-height front wall — often fully glazed — creates a soaring cathedral ceiling effect inside. It brings in enormous amounts of natural light from the front and is a real statement addition to any home. Perfect for south-facing positions.",
    priceFrom: "£12,500",
    priceRange: "£12,500 – £28,000",
    popular: false,
    badge: "Most Light",
    image: "https://readdy.ai/api/search-image?query=gable%20conservatory%20tall%20front%20wall%20vertical%20glass%20modern%20uk%20home%20bright%20interior%20light%20dramatic%20high%20pitched%20roof%20traditional%20white%20aluminium&width=800&height=550&seq=cons-gable&orientation=landscape",
    features: ["Full-height front gable", "Cathedral ceiling effect", "Exceptional natural light", "Striking from street", "Large glazed front", "Airy spacious feel"],
    bestFor: ["South-facing positions", "Light-hungry rooms", "Statement additions", "Large detached homes"],
    roofType: "High-pitched gable roof",
    spaceRating: 4,
    lightRating: 5,
    budgetRating: 3,
    category: "classic",
  },
  {
    id: "hipped-back-gable",
    name: "Hipped-Back Gable",
    tagline: "Gable grandeur with a balanced profile",
    description: "The impressive height of the gable front combined with a low-profile hipped rear roof. A superb balance of drama and practicality.",
    longDesc: "For those who love the drama of the gable front but want a lower-profile, less imposing rear roof, the Hipped-Back Gable is the ideal compromise. You get all the vertical glazing and soaring ceiling at the front, combined with a neatly hipped roof at the back that sits more sympathetically against the house.",
    priceFrom: "£14,000",
    priceRange: "£14,000 – £30,000",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=hipped%20back%20gable%20conservatory%20uk%20home%20aluminium%20white%20glass%20tall%20front%20traditional%20rear%20hip%20roof%20garden%20bright%20extension&width=800&height=550&seq=cons-hbgable&orientation=landscape",
    features: ["Full-height gable front", "Hipped rear profile", "Balanced elevation", "Exceptional front light", "Suitable for most homes", "Premium kerb appeal"],
    bestFor: ["Balanced plot positions", "Corner visibility", "High-end projects", "Detached homes"],
    roofType: "Gable front, hipped rear",
    spaceRating: 4,
    lightRating: 5,
    budgetRating: 2,
    category: "premium",
  },
  {
    id: "orangery",
    name: "Orangery",
    tagline: "Premium glass extension with a real wow factor",
    description: "Part extension, part conservatory. Solid brick or rendered pillars with a flat perimeter ceiling and central glazed roof lantern — a truly premium addition.",
    longDesc: "The orangery bridges the gap between a full extension and a conservatory. Substantial masonry or rendered pillar columns give it a solid, permanent feel while the central roof lantern floods the interior with light. The result is one of the most desirable, high-value additions you can make to a UK home.",
    priceFrom: "£20,000",
    priceRange: "£20,000 – £50,000+",
    popular: false,
    badge: "Premium Choice",
    image: "https://readdy.ai/api/search-image?query=orangery%20premium%20glass%20extension%20brick%20pillars%20roof%20lantern%20light%20interior%20luxury%20uk%20home%20elegant%20garden%20contemporary%20modern%20kitchen%20dining&width=800&height=550&seq=cons-orangery&orientation=landscape",
    features: ["Solid masonry pillars", "Central roof lantern", "Premium appearance", "Solid perimeter roof", "Adds significant value", "Blends with house"],
    bestFor: ["Kitchen-diners", "Premium budgets", "Planning-sensitive areas", "Adding property value"],
    roofType: "Flat perimeter + central lantern",
    spaceRating: 4,
    lightRating: 5,
    budgetRating: 1,
    category: "premium",
  },
  {
    id: "hipped-back-orangery",
    name: "Hipped-Back Orangery",
    tagline: "All the elegance of an orangery, beautifully finished",
    description: "Our Hipped-Back Orangery combines the solid masonry character of the classic orangery with a hipped glass roof section for a uniquely refined look.",
    longDesc: "The Hipped-Back Orangery takes the luxurious feel of the classic orangery — the brick pillars, the premium materials, the substantial feel — and adds a hipped glass roof for a more traditional finish. It sits beautifully between a full extension and a glazed structure, with character and light in equal measure.",
    priceFrom: "£22,000",
    priceRange: "£22,000 – £55,000+",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=hipped%20back%20orangery%20premium%20glass%20extension%20brick%20pillars%20hipped%20roof%20traditional%20uk%20home%20luxury%20garden%20extension%20light%20contemporary&width=800&height=550&seq=cons-hborangery&orientation=landscape",
    features: ["Masonry columns", "Hipped glass roof", "Highly refined look", "Premium materials", "Excellent kerb appeal", "Bespoke character"],
    bestFor: ["Period properties", "Premium projects", "Large rear gardens", "Significant investment"],
    roofType: "Hipped glass + perimeter solid",
    spaceRating: 4,
    lightRating: 4,
    budgetRating: 1,
    category: "premium",
  },
  {
    id: "dwarf-wall",
    name: "Dwarf-Wall",
    tagline: "Traditional brick base, elegant glazing above",
    description: "A solid low brick or rendered wall around the base with full-height glazing panels above. Provides excellent privacy and a more traditional, house-like appearance.",
    longDesc: "The dwarf-wall conservatory is characterised by a low brick or rendered wall — typically 600–900mm high — around the perimeter, with full-height window frames above. This provides better insulation than a full-height glazed design, gives the structure a more substantial, traditional feel, and offers excellent privacy from neighbours.",
    priceFrom: "£10,500",
    priceRange: "£10,500 – £25,000",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=dwarf%20wall%20conservatory%20low%20brick%20base%20glazing%20above%20traditional%20uk%20home%20white%20aluminium%20garden%20extension%20classic%20privacy%20matching%20brickwork&width=800&height=550&seq=cons-dwarfwall&orientation=landscape",
    features: ["Low brick/rendered base wall", "Full-height glazing above", "Better insulation at base", "Traditional appearance", "Enhanced privacy", "Solid substantial feel"],
    bestFor: ["Traditional properties", "Privacy needs", "Corner positions", "Replacing old conservatories"],
    roofType: "Any style roof available",
    spaceRating: 4,
    lightRating: 3,
    budgetRating: 3,
    category: "classic",
  },
  {
    id: "bungalow",
    name: "Conservatory for Bungalows",
    tagline: "Purpose-designed for single-storey homes",
    description: "Specially tailored conservatory designs for bungalows — taking into account lower eave heights and ensuring the finished result looks proportionally correct.",
    longDesc: "Bungalows present unique challenges for conservatory design because the eave heights are lower and the proportions differ from two-storey homes. Our bungalow conservatories are specifically designed to look proportionally perfect on single-storey properties, with roof angles and frame heights carefully calculated for your specific property.",
    priceFrom: "£8,000",
    priceRange: "£8,000 – £20,000",
    popular: false,
    badge: "Bungalow Specialist",
    image: "https://readdy.ai/api/search-image?query=bungalow%20conservatory%20single%20storey%20low%20eave%20white%20aluminium%20uPVC%20single%20level%20uk%20home%20garden%20extension%20brick%20wall%20glass%20natural%20light&width=800&height=550&seq=cons-bungalow&orientation=landscape",
    features: ["Designed for low eave heights", "Correct proportions", "Lean-to or Victorian styles", "Any footprint size", "Full custom design", "Planning advice included"],
    bestFor: ["Bungalows only", "Low eave properties", "Single storey homes", "Retirement properties"],
    roofType: "Lean-to or traditional styles",
    spaceRating: 4,
    lightRating: 4,
    budgetRating: 4,
    category: "specialist",
  },
  {
    id: "p-shape",
    name: "P-Shape",
    tagline: "Two rooms in one — the ultimate extension",
    description: "Combines a lean-to section with a Victorian or Edwardian bay to create a P-shaped footprint. Two distinct spaces in one continuous structure.",
    longDesc: "The P-Shape conservatory is essentially two conservatory designs merged into a single structure — typically a rectangular lean-to section running along one wall and a Victorian or Edwardian bay projecting from the end. The result is a large, versatile space that can serve multiple functions — sitting room at one end, dining at the other.",
    priceFrom: "£15,000",
    priceRange: "£15,000 – £38,000",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=P%20shape%20conservatory%20large%20combined%20lean%20to%20Victorian%20bay%20front%20white%20aluminium%20uk%20home%20two%20room%20garden%20extension%20luxury%20glass%20roof&width=800&height=550&seq=cons-pshape&orientation=landscape",
    features: ["Two-room in one structure", "Victorian or Edwardian bay", "Combined lean-to section", "Large footprint", "Versatile usage", "Maximum garden enjoyment"],
    bestFor: ["Large rear gardens", "Multi-functional spaces", "Dual aspect views", "Big families"],
    roofType: "Combined lean-to + pitched bay",
    spaceRating: 5,
    lightRating: 5,
    budgetRating: 2,
    category: "bespoke",
  },
  {
    id: "l-shape",
    name: "L-Shape",
    tagline: "Wrap-around design for corner positions",
    description: "An L-shaped configuration that wraps around the corner of a house, maximising the use of a corner plot and connecting multiple rear and side aspects.",
    longDesc: "The L-Shape conservatory is the ideal solution when you want to use both a rear and side elevation — perhaps where a standard rectangular design would block a window or doorway. The L-shaped footprint wraps around the corner of the house, bringing light to two aspects of the ground floor and creating a beautiful wrap-around living space.",
    priceFrom: "£18,000",
    priceRange: "£18,000 – £45,000",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=L%20shape%20conservatory%20corner%20wrap%20around%20white%20aluminium%20uk%20home%20two%20elevations%20glass%20roof%20garden%20extension%20luxury%20bespoke%20curved%20corner&width=800&height=550&seq=cons-lshape&orientation=landscape",
    features: ["L-shaped footprint", "Wraps corner of house", "Connects rear & side", "Maximises corner plots", "Bespoke design", "Multiple aspect views"],
    bestFor: ["Corner plots", "Side & rear combined", "Multiple room connections", "Bespoke projects"],
    roofType: "Bespoke combined roof design",
    spaceRating: 5,
    lightRating: 5,
    budgetRating: 2,
    category: "bespoke",
  },
  {
    id: "small",
    name: "Small Conservatory",
    tagline: "Big benefits in a compact footprint",
    description: "Compact conservatories under 3m x 3m — perfect for smaller gardens or where planning restrictions limit the size of an extension.",
    longDesc: "A smaller conservatory doesn't mean a lesser one. Our compact designs under 3m x 3m are carefully proportioned to look great and function brilliantly. They are ideal for smaller gardens, mid-terrace properties, or anywhere that a larger structure might be impractical or require planning permission.",
    priceFrom: "£7,000",
    priceRange: "£7,000 – £18,000",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=small%20compact%20conservatory%20white%20uPVC%20aluminium%20uk%20terraced%20home%20garden%20small%20footprint%20neat%20proportioned%20glass%20roof%20lean%20to%20Victorian&width=800&height=550&seq=cons-small&orientation=landscape",
    features: ["Under 3m x 3m", "Perfect for smaller gardens", "Usually permitted development", "Any style available", "Budget-friendly", "Quick to install"],
    bestFor: ["Terraced homes", "Small gardens", "Tight budgets", "Permitted development"],
    roofType: "Any style available",
    spaceRating: 2,
    lightRating: 3,
    budgetRating: 5,
    category: "specialist",
  },
  {
    id: "3x3",
    name: "3×3 Conservatory",
    tagline: "The standard size — a great starting point",
    description: "The most popular standard conservatory size — 3 metres wide by 3 metres deep. A great all-round choice that gives a proper room-sized space.",
    longDesc: "The 3x3 conservatory — 3 metres wide by 3 metres deep — is the most commonly chosen size and for good reason. It is large enough to feel like a proper room, small enough to be cost-effective, and in most cases falls within permitted development rights without needing planning permission.",
    priceFrom: "£8,500",
    priceRange: "£8,500 – £20,000",
    popular: true,
    badge: "Most Popular Size",
    image: "https://readdy.ai/api/search-image?query=3x3%20standard%20size%20conservatory%20white%20uPVC%20aluminium%20uk%20home%20garden%20Victorian%20Edwardian%20brick%20glass%20roof%20double%20doors%20natural%20light%20cosy&width=800&height=550&seq=cons-3x3&orientation=landscape",
    features: ["3m x 3m footprint", "Usually permitted development", "Most popular size", "Good room-sized space", "All styles available", "Great value"],
    bestFor: ["Most homes", "First-time buyers", "Standard rear extensions", "Value-focused projects"],
    roofType: "Any style available",
    spaceRating: 3,
    lightRating: 3,
    budgetRating: 4,
    category: "specialist",
  },
  {
    id: "large",
    name: "Large Conservatory",
    tagline: "Over 20m² — a genuine extra room",
    description: "Large conservatories over 20 square metres create a genuine additional living space. Perfect for open-plan kitchen-diners or multi-purpose family rooms.",
    longDesc: "A large conservatory over 20 square metres feels like a proper permanent room. It is big enough for a dining table, a sofa, a home office corner — or all three at once. At this scale, we typically recommend an orangery or a solid-roof lantern design to manage solar gain and ensure year-round comfort.",
    priceFrom: "£16,000",
    priceRange: "£16,000 – £40,000+",
    popular: false,
    image: "https://readdy.ai/api/search-image?query=large%20spacious%20conservatory%20over%2020sqm%20open%20plan%20kitchen%20dining%20uk%20home%20white%20aluminium%20glass%20full%20size%20living%20room%20comfortable%20interior%20garden%20view&width=800&height=550&seq=cons-large&orientation=landscape",
    features: ["Over 20 square metres", "Genuine extra room", "Open plan possible", "Planning permission likely", "Multiple style options", "Year-round comfort"],
    bestFor: ["Large rear gardens", "Open-plan living", "Kitchen extensions", "Significant investment"],
    roofType: "Orangery/lantern recommended",
    spaceRating: 5,
    lightRating: 5,
    budgetRating: 2,
    category: "premium",
  },
  {
    id: "replacement",
    name: "Replacement Conservatory",
    tagline: "Transform your existing space",
    description: "Replace an old, cold, or ugly conservatory with a beautiful new one. We can often use the existing base, saving significant cost and disruption.",
    longDesc: "If you already have a conservatory that is too cold in winter, too hot in summer, or simply looking dated, a replacement is the ideal solution. In many cases we can use your existing base slab and dwarf walls, which significantly reduces the cost and disruption compared to building from scratch.",
    priceFrom: "£9,000",
    priceRange: "£9,000 – £25,000",
    popular: false,
    badge: "Existing Base Usable",
    image: "https://readdy.ai/api/search-image?query=replacement%20conservatory%20new%20modern%20white%20aluminium%20installed%20existing%20old%20base%20uk%20home%20garden%20bright%20new%20glass%20solid%20roof%20contemporary&width=800&height=550&seq=cons-replacement&orientation=landscape",
    features: ["Existing base re-usable", "No planning needed", "Faster installation", "New glass & frames", "Solid roof upgrade option", "Dramatic transformation"],
    bestFor: ["Existing conservatories", "Cold/hot existing rooms", "Dated appearance", "Budget-conscious upgrades"],
    roofType: "Any replacement style",
    spaceRating: 3,
    lightRating: 4,
    budgetRating: 4,
    category: "specialist",
  },
  {
    id: "bespoke",
    name: "Bespoke Conservatory",
    tagline: "Fully custom — your exact vision",
    description: "When no standard design quite fits, we design and build completely bespoke conservatories to your exact requirements — any shape, any size, any style.",
    longDesc: "Our bespoke conservatory service is for projects that do not fit neatly into a standard style. Whether it is an unusual house shape, a specific planning requirement, a complex site, or simply a unique vision, we design and build entirely from scratch. Every aspect — footprint, height, roof style, materials, colours — is specified by you.",
    priceFrom: "£20,000",
    priceRange: "£20,000 – £70,000+",
    popular: false,
    badge: "Fully Custom",
    image: "https://readdy.ai/api/search-image?query=bespoke%20custom%20conservatory%20unique%20design%20luxury%20aluminium%20uk%20home%20garden%20architecture%20specialist%20bespoke%20glass%20extension%20complex%20shape%20premium%20high%20end&width=800&height=550&seq=cons-bespoke&orientation=landscape",
    features: ["Any shape or footprint", "Fully custom design", "Architect collaboration possible", "Complex sites welcomed", "Premium materials", "Unique result guaranteed"],
    bestFor: ["Complex sites", "Unusual house shapes", "Planning-sensitive zones", "Premium no-compromise projects"],
    roofType: "Fully custom",
    spaceRating: 5,
    lightRating: 5,
    budgetRating: 1,
    category: "bespoke",
  },
];

export const conservatoryCategories = [
  { id: "all", label: "All Styles" },
  { id: "classic", label: "Classic" },
  { id: "premium", label: "Premium" },
  { id: "bespoke", label: "Bespoke" },
  { id: "specialist", label: "Specialist" },
];
